import { useEffect, useState, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Monitor, Smartphone, SmartphoneNfc } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { validateRut } from "@/lib/rut";
import { formatNombreArea } from "@/lib/area";
import { RutInput } from "@/components/RutInput";
import { Tip } from "@/components/ds/Tip";
import { useUser } from "@/contexts/UserContext";
import type { AreaSummary } from "@/types/area";
import type {
  CargoSummary,
  ContratoSummary,
  Trabajador,
} from "@/types/trabajador";

export interface TrabajadorFormProps {
  mode: "create" | "edit";
  trabajador?: Trabajador;
  /** Se invoca al terminar de guardar con el trabajador creado/actualizado. */
  onSaved: (trabajador: Trabajador) => void;
  /**
   * Render prop para las acciones del formulario (botón Guardar). Permite que
   * cada consumidor (diálogo de edición rápida vs. Tab1 de la Ficha) decida su
   * propio contenedor/estilo, manteniendo el submit dentro del `<form>`.
   */
  renderFooter: (state: { isSubmitting: boolean }) => ReactNode;
  /** Override del className del `<form>` — por defecto, layout simple en columna. */
  formClassName?: string;
  /**
   * Autofoco en el primer campo (Nombre). Por defecto `true` — comportamiento
   * del diálogo de edición rápida. La Ficha a página completa lo desactiva para
   * no robar el foco (ni forzar scroll) al montar el Tab1.
   */
  autoFocusFirst?: boolean;
  /**
   * Cómo se agrupan los campos.
   *
   * - `dialogo` (por defecto): una columna continua, para el drawer de alta.
   * - `secciones`: tres tarjetas —Datos personales · Cargo, áreas y contrato ·
   *   Estado y accesos— como en la Ficha del trabajador.
   *
   * Es SÓLO presentación: estado, validación y submit son los mismos en ambos.
   * Partir el formulario en dos componentes habría significado dos lugares
   * donde mantener las mismas reglas.
   */
  layout?: "dialogo" | "secciones";
}

/** Tarjeta con encabezado en versalitas — el molde de sección de la Ficha. */
function SeccionFicha({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="bg-card space-y-4 rounded-xl border px-4 py-4 sm:px-5">
      <h3 className="text-soft text-[11px] font-semibold tracking-[.08em] uppercase">{titulo}</h3>
      {children}
    </section>
  );
}

/**
 * Cuerpo de campos del formulario de trabajador, extraído de
 * `TrabajadorFormDrawer` (Trabajadores.tsx) para reutilizarlo también en
 * Tab1 · Info de la Ficha del trabajador. La validación mirror-ea
 * `PersonaRequest` (fc-api) y el payload/contrato de la API NO cambia.
 */
export function TrabajadorForm({
  mode,
  trabajador,
  onSaved,
  renderFooter,
  formClassName,
  autoFocusFirst = true,
  layout = "dialogo",
}: TrabajadorFormProps) {
  const { user } = useUser();
  const esElPropioUsuario =
    mode === "edit" && trabajador?.user_id != null && trabajador.user_id === user?.id;
  const [nombre, setNombre] = useState(trabajador?.nombre ?? "");
  const [apellido, setApellido] = useState(trabajador?.apellido ?? "");
  const [rut, setRut] = useState(trabajador?.rut ?? "");
  const [telefono, setTelefono] = useState(trabajador?.telefono ?? "");
  const [email, setEmail] = useState(trabajador?.email ?? "");
  // Confirmación de email: en edición se pre-llena con el email actual, así no
  // molesta si no lo cambian; si lo cambian, deben re-confirmar.
  const [confirmEmail, setConfirmEmail] = useState(trabajador?.email ?? "");
  const [cargoId, setCargoId] = useState<string>(
    trabajador?.cargo ? String(trabajador.cargo.id) : "",
  );
  const [activo, setActivo] = useState<boolean>(trabajador?.activo ?? true);
  // Confirmación al inactivar a un trabajador que tiene la app: se le retirará
  // la licencia al guardar (el backend la libera en la transición activo->off).
  const [confirmInactivar, setConfirmInactivar] = useState(false);
  const [canAccessWeb, setCanAccessWeb] = useState<boolean>(
    trabajador?.user_can_access_web ?? false,
  );
  const [areas, setAreas] = useState<AreaSummary[]>([]);
  const [areasIds, setAreasIds] = useState<string[]>(
    trabajador?.areas?.map((a) => String(a.id)) ?? [],
  );
  const [cargos, setCargos] = useState<CargoSummary[]>([]);
  const [contratos, setContratos] = useState<ContratoSummary[]>([]);
  const [contratosIds, setContratosIds] = useState<string[]>(
    trabajador?.contratos?.map((c) => String(c.id)) ?? [],
  );
  const [isLoadingCatalogos, setIsLoadingCatalogos] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRutError, setShowRutError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [areasRes, cargosRes, contratosRes] = await Promise.all([
          api.get<{ data: AreaSummary[] }>("/areas?all=1"),
          api.get<{ data: CargoSummary[] }>("/cargos?all=1"),
          api.get<{ data: ContratoSummary[] }>("/comercial/contratos"),
        ]);
        if (cancelled) return;
        setAreas(areasRes.data);
        setCargos(cargosRes.data);
        setContratos(contratosRes.data);

        // Filtramos los contratos pre-poblados para que sólo queden los que
        // siguen siendo válidos (la empresa actual es contratista). Esto cubre
        // pivots legacy donde quedaron contratos en los que la empresa no es
        // la contratista.
        if (mode === "edit" && trabajador) {
          const idsValidos = new Set(contratosRes.data.map((c) => String(c.id)));
          const originales = trabajador.contratos.map((c) => String(c.id));
          const filtrados = originales.filter((id) => idsValidos.has(id));
          if (filtrados.length !== originales.length) {
            toast.warning(
              "Se omitieron contratos asignados que ya no son válidos para tu empresa. Revisa la selección.",
            );
          }
          setContratosIds(filtrados);
        }
      } catch {
        toast.error("No se pudieron cargar los catálogos de áreas, cargos y contratos.");
      } finally {
        if (!cancelled) setIsLoadingCatalogos(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, trabajador]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim() || !apellido.trim() || !rut.trim()) {
      toast.error("Nombre, apellido y RUT son requeridos.");
      return;
    }
    if (!validateRut(rut)) {
      setShowRutError(true);
      toast.error("El RUT del trabajador es inválido.");
      return;
    }
    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      toast.error("El email es obligatorio.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailTrimmed) || emailTrimmed.length > 255) {
      toast.error("Ingresa un email válido.");
      return;
    }
    if (emailTrimmed !== confirmEmail.trim()) {
      toast.error("Los emails no coinciden. Verifica la confirmación.");
      return;
    }
    if (!cargoId) {
      toast.error("Asigna un cargo al trabajador.");
      return;
    }
    // El contrato solo es exigible cuando hay alguno seleccionable. Si la empresa
    // solo tiene su contrato propio (sin mandantes), el selector queda vacío y el
    // backend asigna el contrato base automáticamente.
    if (contratosOptions.length > 0 && contratosIds.length === 0) {
      toast.error("Asigna al menos un contrato.");
      return;
    }
    // Las áreas son un clasificador OPCIONAL — no bloquean el guardado. Un
    // trabajador puede quedar sin ninguna. Enviamos areas_ids siempre (incluso
    // vacío) para que deseleccionar todo efectivamente limpie sus áreas.

    const payload: Record<string, unknown> = {
      nombre: nombre.trim(),
      apellido: apellido.trim(),
      rut: rut.trim(),
      telefono: telefono.trim() || null,
      email: email.trim(),
      cargo_id: Number(cargoId),
      activo,
      areas_ids: areasIds.map(Number),
    };

    // Solo enviamos contratos_ids si hay selección. Si va vacío omitimos la clave
    // para que el backend asigne el contrato base propio (create) o conserve los
    // actuales (update) — la regla del "contrato por defecto" vive en el backend.
    if (contratosIds.length > 0) {
      payload.contratos_ids = contratosIds.map(Number);
    }

    payload.user_can_access_web = canAccessWeb;

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const res = await api.post<{ data: Trabajador }>("/personas", payload);
        toast.success("Trabajador creado.");
        onSaved(res.data);
      } else if (trabajador) {
        const res = await api.put<{ data: Trabajador }>(
          `/personas/${trabajador.id}`,
          payload,
        );
        toast.success("Trabajador actualizado.");
        onSaved(res.data);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
      } else {
        toast.error("No se pudo guardar el trabajador.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const areasOptions = areas.map((a) => ({ value: String(a.id), label: formatNombreArea(a) }));
  // Regla del selector de contratos:
  // - Si la empresa tiene algún contrato con un mandante (además del propio),
  //   se muestran TODOS, incluido el contrato propio "Operación interna", para
  //   poder elegir bajo cuál queda el trabajador.
  // - Si solo existe el contrato propio (sin mandantes), el selector queda
  //   vacío y el backend asigna ese contrato base automáticamente.
  // Se deriva de la data (no del flag empresa.tiene_mandantes, que el login web
  // no incluye y quedaba en false ocultando el propio indebidamente).
  const esContratoPropio = (c: ContratoSummary) => {
    const mandante = c.relacion_empresa?.mandante;
    const contratista = c.relacion_empresa?.contratista;
    return !!(mandante && contratista && mandante.id === contratista.id);
  };
  const tieneContratoConMandante = contratos.some((c) => !esContratoPropio(c));
  const contratosOptions = (tieneContratoConMandante ? contratos : []).map((c) => {
    const mandante = c.relacion_empresa?.mandante;
    const sufijo = esContratoPropio(c)
      ? "Operación interna"
      : mandante?.nombre
        ? `Mandante: ${mandante.nombre}`
        : "";
    return {
      value: String(c.id),
      label: sufijo ? `${c.nombre_referencia} · ${sufijo}` : c.nombre_referencia,
    };
  });
  // El contrato solo es obligatorio cuando hay alguno seleccionable. Sin opciones
  // (empresa con solo su contrato propio) el backend asigna el base por defecto.
  const contratosRequerido = contratosOptions.length > 0;

  // ── Campos ────────────────────────────────────────────────────────────────
  // Declarados una sola vez y ordenados después por cada layout. Los dos
  // consumidores (el drawer de alta y la Ficha) comparten estado, validación y
  // submit: lo único que cambia es cómo se agrupan en pantalla.

  const campoNombre = (
    <div className="space-y-2">
      <Label htmlFor="nombre">Nombre</Label>
      <Input
        id="nombre"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        placeholder="Juan"
        disabled={isSubmitting}
        autoFocus={autoFocusFirst}
      />
    </div>
  );

  const campoApellido = (
    <div className="space-y-2">
      <Label htmlFor="apellido">Apellidos</Label>
      <Input
        id="apellido"
        value={apellido}
        onChange={(e) => setApellido(e.target.value)}
        placeholder="Pérez"
        disabled={isSubmitting}
      />
    </div>
  );

  const campoRut = (
    <div className="space-y-2" data-tour="trabajador-rut">
      <Label htmlFor="rut">RUT</Label>
      <RutInput
        id="rut"
        value={rut}
        onChange={setRut}
        placeholder="12.345.678-9"
        disabled={isSubmitting}
        validate
        showError={showRutError}
      />
    </div>
  );

  const campoTelefono = (
    <div className="space-y-2">
      <Label htmlFor="telefono">Teléfono</Label>
      <Input
        id="telefono"
        value={telefono}
        onChange={(e) => setTelefono(e.target.value)}
        placeholder="+56 9 ..."
        disabled={isSubmitting}
      />
    </div>
  );

  const campoEmail = (
    <div className="space-y-2">
      <Label htmlFor="email">
        Correo <span className="text-destructive">*</span>
      </Label>
      <Input
        id="email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="trabajador@ejemplo.cl"
        disabled={isSubmitting}
        required
      />
      <p className="text-muted-foreground text-xs">
        Aquí llegan sus capacitaciones y recordatorios.
      </p>
    </div>
  );

  const campoConfirmEmail = (
    <div className="space-y-2">
      <Label htmlFor="confirmEmail">
        Confirmar correo <span className="text-destructive">*</span>
      </Label>
      <Input
        id="confirmEmail"
        type="email"
        value={confirmEmail}
        onChange={(e) => setConfirmEmail(e.target.value)}
        placeholder="trabajador@ejemplo.cl"
        disabled={isSubmitting}
        required
      />
      {confirmEmail.trim().length > 0 && email.trim() !== confirmEmail.trim() && (
        <p className="text-destructive text-xs">Los correos no coinciden.</p>
      )}
    </div>
  );

  const campoCargo = (
    <div className="space-y-2" data-tour="trabajador-cargo">
      <Label htmlFor="cargo">
        Cargo <span className="text-destructive">*</span>
      </Label>
      <Select
        value={cargoId}
        onValueChange={setCargoId}
        disabled={isSubmitting || isLoadingCatalogos}
      >
        <SelectTrigger id="cargo" className="w-full">
          <SelectValue placeholder={isLoadingCatalogos ? "Cargando..." : "Seleccionar cargo"} />
        </SelectTrigger>
        <SelectContent>
          {cargos.map((c) => (
            <SelectItem key={c.id} value={String(c.id)}>
              {c.nombre}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        Define qué programas de capacitación le corresponden.
      </p>
    </div>
  );

  const campoContratos = (
    <div className="space-y-2">
      <Label>
        Contratos {contratosRequerido && <span className="text-destructive">*</span>}
      </Label>
      <MultiSelect
        options={contratosOptions}
        value={contratosIds}
        onChange={setContratosIds}
        placeholder={
          isLoadingCatalogos
            ? "Cargando contratos..."
            : contratosOptions.length === 0
              ? "No hay contratos disponibles"
              : "Selecciona contratos"
        }
        emptyText="Sin contratos que coincidan."
        disabled={isSubmitting || isLoadingCatalogos || contratosOptions.length === 0}
      />
      <p className="text-muted-foreground text-xs">
        {contratosRequerido
          ? "Cada trabajador debe estar bajo al menos un contrato (Operación interna o de un mandante)."
          : "Se asignará automáticamente al contrato de operación interna de tu empresa."}
      </p>
    </div>
  );

  const campoAreas = (
    <div className="space-y-2" data-tour="trabajador-areas">
      <Label>Áreas</Label>
      <MultiSelect
        options={areasOptions}
        value={areasIds}
        onChange={setAreasIds}
        placeholder={
          isLoadingCatalogos
            ? "Cargando áreas..."
            : areasOptions.length === 0
              ? "No hay áreas creadas todavía"
              : "Selecciona áreas"
        }
        emptyText="Sin áreas que coincidan."
        disabled={isSubmitting || isLoadingCatalogos || areasOptions.length === 0}
      />
      <p className="text-muted-foreground text-xs">
        Opcional. Clasifican al trabajador para filtros y reportes.
      </p>
    </div>
  );

  const filaActivo = (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
      <div className="space-y-0.5">
        <Label htmlFor="activo" className="block text-sm">
          Trabajador activo
        </Label>
        <p className="text-muted-foreground text-xs leading-[1.45]">
          Sale de la dotación operativa y de nuevas capacitaciones, y se libera su licencia de
          app para otro trabajador. Su historial y evidencia se conservan.
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <Badge variant={activo ? "success" : "neutral"}>{activo ? "Activo" : "Inactivo"}</Badge>
        <Switch
          id="activo"
          checked={activo}
          onCheckedChange={(v) => {
            // Si tiene la app, confirmar antes de apagar (perderá la licencia).
            if (!v && trabajador?.tiene_licencia) {
              setConfirmInactivar(true);
            } else {
              setActivo(v);
            }
          }}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );

  const filaAppMovil =
    mode === "edit" && trabajador ? (
      <div className="flex items-center gap-3 rounded-lg border p-3">
        {trabajador.tiene_licencia ? (
          <SmartphoneNfc className="text-primary h-4 w-4 shrink-0" />
        ) : (
          <Smartphone className="text-muted-foreground h-4 w-4 shrink-0" />
        )}
        <div className="min-w-0 space-y-0.5">
          <div className="text-sm font-medium">App móvil</div>
          <p className="text-muted-foreground text-xs">
            {trabajador.tiene_licencia ? "Con app móvil" : "Sin app móvil"} · las licencias se
            administran en Resumen de Cupos.
          </p>
        </div>
      </div>
    ) : null;

  const filaAccesoWeb = (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
      <div className="space-y-0.5">
        <Label htmlFor="can-access-web" className="flex items-center gap-2 text-sm">
          <Monitor className="h-4 w-4" />
          Acceso al panel web
        </Label>
        <p className="text-muted-foreground text-xs leading-[1.45]">
          Permite que esta persona ingrese al panel web de esta empresa con su correo y contraseña.
          Es independiente del acceso a la app móvil. Al activarlo por primera vez le mandamos un
          email para que configure su contraseña.
        </p>
      </div>
      {esElPropioUsuario && canAccessWeb ? (
        <Tip
          texto="No es posible revocar tu propio acceso al panel web. Pide a otro administrador que lo haga por ti."
          lado="left"
          claseEnvoltorio="shrink-0"
        >
          <Switch id="can-access-web" checked={canAccessWeb} disabled />
        </Tip>
      ) : (
        <Switch
          id="can-access-web"
          checked={canAccessWeb}
          onCheckedChange={setCanAccessWeb}
          disabled={isSubmitting}
          className="shrink-0"
        />
      )}
    </div>
  );

  /**
   * Grilla que ENVUELVE en vez de comprimirse — regla del DS para formularios.
   *
   * El mínimo de 320px no es arbitrario: con 220px entraban los seis campos de
   * "Datos personales" en una sola fila estirada, y el diseño los agrupa de a
   * cuatro. A 320px la cuenta da 4 columnas en un escritorio normal, 3 en un
   * portátil y 1 en teléfono, sin fijar nunca un número de columnas a mano.
   */
  const grilla = "grid gap-3 sm:gap-4 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]";

  return (
    <>
      <form onSubmit={submit} className={formClassName ?? "space-y-4"}>
        {layout === "secciones" ? (
          <>
            <SeccionFicha titulo="Datos personales">
              <div className={grilla}>
                {campoNombre}
                {campoApellido}
                {campoRut}
                {campoTelefono}
                {campoEmail}
                {campoConfirmEmail}
              </div>
            </SeccionFicha>

            <SeccionFicha titulo="Cargo, áreas y contrato">
              <div className={grilla}>
                {campoCargo}
                {campoAreas}
                {campoContratos}
              </div>
            </SeccionFicha>

            <SeccionFicha titulo="Estado y accesos">
              <div className="space-y-3">
                {filaActivo}
                {filaAppMovil}
                {filaAccesoWeb}
              </div>
              {renderFooter({ isSubmitting })}
            </SeccionFicha>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {campoNombre}
              {campoApellido}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {campoRut}
              {campoTelefono}
            </div>
            {campoEmail}
            {campoConfirmEmail}
            {campoCargo}
            {campoContratos}
            {campoAreas}
            {filaActivo}
            {filaAppMovil}
            {filaAccesoWeb}
            {renderFooter({ isSubmitting })}
          </>
        )}
      </form>

      <AlertDialog open={confirmInactivar} onOpenChange={setConfirmInactivar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inactivar trabajador</AlertDialogTitle>
            <AlertDialogDescription>
              {trabajador ? (
                <>
                  Al guardar,{" "}
                  <strong>
                    {trabajador.nombre} {trabajador.apellido}
                  </strong>{" "}
                  quedará inactivo y se le <strong>retirará la licencia de la app</strong>{" "}
                  (el cupo vuelve a quedar disponible). Podrás reasignarla desde Licencias.
                  El acceso al panel web, si lo tiene, se conserva.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => setActivo(false)}>
              Inactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
